/**
 * Picks the buy-points offer that best satisfies a points target from a list
 * of available offers.
 *
 * Selection rules, in order:
 *   1. Filter out ineligible offers (`isEligibleNow === false`). Offers without
 *      the field — e.g. those returned by the logged-out public endpoint —
 *      are treated as eligible.
 *   2. Return the smallest eligible offer (by `totalPoints`, base + bonus)
 *      whose `totalPoints >= requiredPoints`.
 *   3. If no eligible offer is big enough, return the largest eligible offer
 *      so the member can still close as much of the gap as possible in one
 *      purchase.
 *
 * @param offers - Available buy-points offers, in any order.
 * @param requiredPoints - The points target to cover (typically a member's
 *   reward-redemption deficit).
 * @returns The chosen offer, or `undefined` when `requiredPoints` is
 *   non-positive, no offers were passed, or no offers are eligible.
 */
function loyaltyPointsPurchasePickBestOffer(
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>,
  requiredPoints: number,
): App.LoyaltyPurchasePointsOffer | undefined {
  if (requiredPoints <= 0 || offers.length === 0) return undefined
  const eligibleAsc = offers
    .filter(o => o.isEligibleNow !== false)
    .sort((a, b) => a.totalPoints - b.totalPoints)
  if (eligibleAsc.length === 0) return undefined
  return eligibleAsc.find(o => o.totalPoints >= requiredPoints) ?? eligibleAsc[eligibleAsc.length - 1]
}

export default loyaltyPointsPurchasePickBestOffer
